import { isPage, isSection, isContainedView } from "../../../../../common/utils";
import { PAGE_TYPES } from "../../../../../common/constants";
import { isClmAllowedPlugin } from "../../../../../common/clm";

// Pure export-time counterparts of _visor/utils/visorNavigation.js. That module
// reads IdealStudio.State globals, which do not exist in the editor/export
// context — every function here takes navItemsIds/navItemsById explicitly and
// mirrors the visor semantics (skip hidden, section → first visible page, no
// wrap). Contract: docs/superpowers/plans/2026-09-08-clm-p1-oneclick-nav-quickadd.md.

// Static stage every exported page is laid out on (the artifact stage basis,
// common/HtmlArtifactShadowHost.jsx). px-unit box geometry is converted against
// it and flagged `approx` — the editor's own px basis is not 1920 wide.
export const CLM_STAGE = { width: 1920, height: 1080 };

const MIN_TOUCH_PX = 48;
// Bottom 10% of the stage: where compliance furniture (references, codes, QR)
// sits in pharma decks, baked into the page image. The check is about OUR nav
// chrome covering that print, not about authored content living there (a
// disclaimer footer text box is expected in the band, not a problem). A row
// of 3+ click zones is a deliberate dock, not occlusion.
const FURNITURE_BAND_TOP = 90;
const DOCK_MIN_HOTSPOTS = 3;

export function listClmPages(navItemsIds, navItemsById) {
  return (navItemsIds || []).filter((id) => {
    const navItem = navItemsById && navItemsById[id];
    return isPage(id) && navItem && !navItem.hidden;
  });
}

// Mirrors findFirstValidPageRecursive (visorNavigation.js:7-44).
export function firstVisiblePageIn(sectionId, navItemsById) {
  if (!sectionId || !isSection(sectionId)) return null;
  const section = navItemsById && navItemsById[sectionId];
  const children = (section && section.children) || [];
  for (let i = 0; i < children.length; i++) {
    const childId = children[i];
    const child = navItemsById[childId];
    if (!child || child.hidden) continue;
    if (isPage(childId)) return childId;
    if (isSection(childId)) {
      const found = firstVisiblePageIn(childId, navItemsById);
      if (found) return found;
    }
  }
  return null;
}

export function marksOf(marksById, boxId) {
  return Object.keys(marksById || {})
    .map((id) => marksById[id])
    .filter((mark) => mark && mark.origin === boxId);
}

function pageTarget(pageId, pages) {
  const index = pages.indexOf(pageId);
  return index === -1
    ? { kind: "none", reason: "unresolved" }
    : { kind: "page", pageId: pageId, pageNumber: index + 1 };
}

// Allowlist for OneClick "external" targets: the resolved url is emitted
// straight into an inline `window.open(...)` handler (clmSerializers.js
// serializeHotspot) in the customer's origin, so a `javascript:` or
// `data:text/html;base64,...` value here is code execution, not a bad link.
// Tab/newline/CR are stripped from the WHOLE string (not just the leading
// edge) before the scheme check because browsers do the same before parsing
// a URL — otherwise `\tj\na\tv\ta\tscript:` slips past a naive prefix test.
const SAFE_EXTERNAL_URL = /^(https?:|mailto:|tel:)/i;
function isSafeExternalUrl(url) {
  const stripped = String(url || "").replace(/[\t\n\r]/g, "");
  const trimmed = stripped.replace(/^[\s\x00-\x1f]+/, "");
  return SAFE_EXTERNAL_URL.test(trimmed);
}

export function resolveMarkTarget(mark, originPageId, navItemsIds, navItemsById) {
  const pages = listClmPages(navItemsIds, navItemsById);
  const index = pages.indexOf(originPageId);
  const connection = mark && mark.connection;
  switch (mark && mark.connectMode) {
    case "next":
      return index !== -1 && index < pages.length - 1
        ? pageTarget(pages[index + 1], pages)
        : { kind: "none", reason: "boundary" };
    case "prev":
      return index > 0 ? pageTarget(pages[index - 1], pages) : { kind: "none", reason: "boundary" };
    case "goToSection":
      return pageTarget(firstVisiblePageIn(connection, navItemsById), pages);
    case "existing":
    case "new":
      if (isContainedView(connection)) return { kind: "overlay", cvId: connection };
      if (isSection(connection))
        return pageTarget(firstVisiblePageIn(connection, navItemsById), pages);
      return pageTarget(connection, pages);
    case "external":
      if (!connection) return { kind: "none", reason: "unresolved" };
      return isSafeExternalUrl(connection)
        ? { kind: "external", url: connection }
        : { kind: "none", reason: "unsafe_external_url" };
    default:
      return { kind: "none", reason: "unsupported" };
  }
}

function toPercent(value, unit, basis) {
  if (value === "auto" || value === undefined || value === null || value === "") return null;
  const n = parseFloat(value);
  if (isNaN(n)) return null;
  const isPx = unit === "px" || (!unit && typeof value === "string" && value.indexOf("px") !== -1);
  return isPx ? { value: (n / basis) * 100, approx: true } : { value: n, approx: false };
}

export function boxGeometry(box, toolbar) {
  const position = (box && box.position) || {};
  const structure = (toolbar && toolbar.structure) || {};
  const left = toPercent(position.x, null, CLM_STAGE.width) || { value: 0, approx: false };
  const top = toPercent(position.y, null, CLM_STAGE.height) || { value: 0, approx: false };
  const width = toPercent(structure.width, structure.widthUnit, CLM_STAGE.width);
  const height = toPercent(structure.height, structure.heightUnit, CLM_STAGE.height);
  return {
    left: left.value,
    top: top.value,
    width: width ? width.value : null,
    height: height ? height.value : null,
    rotation: structure.rotation || 0,
    approx: left.approx || top.approx || !!(width && width.approx) || !!(height && height.approx),
  };
}

export function sceneTitle(pageId, navItemsById, viewToolbarsById) {
  const viewToolbar = viewToolbarsById && viewToolbarsById[pageId];
  const navItem = navItemsById && navItemsById[pageId];
  return (viewToolbar && viewToolbar.viewName) || (navItem && navItem.name) || pageId;
}

// PAXERA convention (reference config: key order path/brandID/title/order,
// brandID only on page-1).
export function buildClmConfigJson(pageIds, navItemsById, viewToolbarsById, clm, updatedAt) {
  const brandId =
    clm && clm.brandId !== undefined && clm.brandId !== null ? String(clm.brandId) : "";
  return {
    slides: pageIds.map((pageId, index) => ({
      path: "page-" + (index + 1),
      brandID: index === 0 ? brandId : "",
      title: sceneTitle(pageId, navItemsById, viewToolbarsById),
      order: index + 1,
    })),
    updatedAt: updatedAt,
  };
}

const TR_MAP = {
  ı: "i",
  İ: "I",
  ğ: "g",
  Ğ: "G",
  ş: "s",
  Ş: "S",
  ç: "c",
  Ç: "C",
  ö: "o",
  Ö: "O",
  ü: "u",
  Ü: "U",
};

// English-safe file-name token: transliterate Turkish, strip other diacritics,
// collapse everything non-alphanumeric into "_", upper-case (D7 / ruling 6).
export function sanitizeClmToken(text) {
  return String(text || "")
    .replace(/[ıİğĞşŞçÇöÖüÜ]/g, (ch) => TR_MAP[ch])
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^A-Za-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toUpperCase();
}

export function parseClmVersion(version) {
  const match = /^(\d+)(?:\.(\d+))?/.exec(
    String(version === undefined || version === null ? "" : version)
  );
  if (!match) return { major: 1, minor: 0 };
  return { major: parseInt(match[1], 10), minor: match[2] ? parseInt(match[2], 10) : 0 };
}

export function buildClmZipName(globalConfig, now = new Date()) {
  const clm = (globalConfig && globalConfig.clm) || {};
  const product =
    sanitizeClmToken(clm.productName) ||
    sanitizeClmToken(globalConfig && globalConfig.title) ||
    "CLM";
  const version = parseClmVersion(clm.version);
  return (
    product + "_KOKPIT_" + now.getFullYear() + "_v" + version.major + "." + version.minor + ".zip"
  );
}

// ExportModal's editable zip-name field: same Turkish transliteration as
// sanitizeClmToken (English alphabet, no spaces) but keeps case
// and dots, unlike sanitizeClmToken's all-caps/underscore-only token format,
// so an edited "PaxeraV2.zip" doesn't get mangled into "PAXERAV2_ZIP".
export function sanitizeClmZipFileName(name) {
  const base = String(name || "")
    .trim()
    .replace(/\.zip$/i, "")
    .replace(/[ıİğĞşŞçÇöÖüÜ]/g, (ch) => TR_MAP[ch])
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^A-Za-z0-9._-]+/g, "_")
    .replace(/^[_.]+|[_.]+$/g, "");
  return (base || "CLM") + ".zip";
}

// D9: each successful export bumps clmConfig.version (minor) — the caller
// persists it through changeGlobalConfig("clm", ...) + save.
export function bumpClmVersion(clm) {
  const version = parseClmVersion(clm && clm.version);
  return { ...(clm || {}), version: version.major + "." + (version.minor + 1) };
}

function isBackgroundUrl(value) {
  return /^(https?:\/\/|data:|url\()/i.test(String(value || "").trim());
}

function isNavTarget(target) {
  return target.kind === "page" || target.kind === "overlay" || target.kind === "external";
}

// Blocking + warning-only checks (spec §3.5, ruling 5). Pure: no fetches — the
// "every image fetched OK" rule is enforced by the builder, which reports
// failures with the same Problem shape.
export function validateClmState(state) {
  const errors = [];
  const warnings = [];
  const navItemsIds = state.navItemsIds || [];
  const navItemsById = state.navItemsById || {};
  const boxesById = state.boxesById || {};
  const toolbars = state.pluginToolbarsById || {};
  const marksById = state.marksById || {};
  const viewToolbars = state.viewToolbarsById || {};
  const pages = listClmPages(navItemsIds, navItemsById);

  if (pages.length === 0) {
    errors.push({ code: "no_pages" });
    return { errors, warnings };
  }

  pages.forEach((pageId, index) => {
    const navItem = navItemsById[pageId];
    const where = {
      sceneId: pageId,
      scene: sceneTitle(pageId, navItemsById, viewToolbars),
      pageNumber: index + 1,
    };
    if (navItem.type !== PAGE_TYPES.SLIDE) {
      errors.push({ code: "document_scene", ...where });
      return;
    }
    // background/themeHtml are mutually exclusive (reducers/view_toolbars_by_id.es6:126-130).
    // A themed scene exports with no background field at all — falls back to a
    // plain black/white page (clmZipBuilder.js containerBackground) with no
    // signal to the author unless we warn here explicitly.
    const viewToolbar = viewToolbars[pageId] || {};
    if (viewToolbar.themeHtml) {
      warnings.push({ code: "theme_not_exported", ...where });
    } else if (viewToolbar.background && isBackgroundUrl(viewToolbar.background)) {
      warnings.push({ code: "background_not_exported", ...where });
    }

    let pageImageInBand = false;
    let hotspotsInBand = 0;
    (navItem.boxes || []).forEach((boxId) => {
      const box = boxesById[boxId];
      const toolbar = toolbars[boxId];
      if (!box || !toolbar) return;
      const pluginId = toolbar.pluginId;
      if (!isClmAllowedPlugin(pluginId)) {
        warnings.push({ code: "unsupported_plugin", plugin: pluginId, boxId, ...where });
        return;
      }
      const geometry = boxGeometry(box, toolbar);
      if (geometry.approx) warnings.push({ code: "approx_geometry", boxId, ...where });

      const bottom = geometry.top + (geometry.height === null ? 0 : geometry.height);
      if (pluginId === "HotspotImages" || pluginId === "Shapes") {
        if (bottom > FURNITURE_BAND_TOP) pageImageInBand = true;
      } else if (pluginId === "OneClick" && bottom > FURNITURE_BAND_TOP) {
        hotspotsInBand++;
      }

      if (pluginId === "HtmlContent") {
        const html = toolbar.state && toolbar.state._html;
        if (typeof html === "string" && /^https?:\/\//.test(html)) {
          warnings.push({ code: "external_html", boxId, ...where });
        }
        return;
      }
      if (pluginId !== "OneClick") return;

      const marks = marksOf(marksById, boxId);
      const targets = marks.map((mark) =>
        resolveMarkTarget(mark, pageId, navItemsIds, navItemsById)
      );
      if (targets.length === 0) warnings.push({ code: "empty_click_zone", boxId, ...where });
      if (targets.filter(isNavTarget).length > 1)
        errors.push({ code: "multi_nav", boxId, ...where });
      targets.forEach((target, i) => {
        if (target.kind !== "none") return;
        if (target.reason === "boundary") {
          warnings.push({ code: "boundary_nav", boxId, ...where });
        } else if (target.reason === "unsupported") {
          // A real, UI-selectable OneClick option (e.g. "popup") the static
          // exporter just can't wire up — degrades that one hotspot to inert,
          // not a reason to block the whole export.
          warnings.push({
            code: "unsupported_connect_mode",
            boxId,
            connectMode: marks[i] && marks[i].connectMode,
            ...where,
          });
        } else if (target.reason === "unsafe_external_url") {
          // Author typed a link the exporter refuses to navigate to (e.g.
          // javascript:/data:) — non-blocking so the rest of the deck still
          // exports, but the hotspot goes inert and the author is told why.
          warnings.push({ code: "unsafe_external_url", boxId, ...where });
        } else {
          errors.push({ code: "missing_target", boxId, ...where });
        }
      });
      const widthPx = geometry.width === null ? Infinity : (geometry.width / 100) * CLM_STAGE.width;
      const heightPx =
        geometry.height === null ? Infinity : (geometry.height / 100) * CLM_STAGE.height;
      if (widthPx < MIN_TOUCH_PX || heightPx < MIN_TOUCH_PX) {
        warnings.push({ code: "small_touch_target", boxId, ...where });
      }
    });

    // NOT "any element in the band" — that flagged an author's own compliance
    // footer (e.g. a BasicText disclaimer) for reproducing the exact content
    // the band exists for. Only warn when our own nav chrome (1-2 stray click
    // zones, short of a deliberate 3+ dock) sits over a page whose own image
    // already reaches into the band — i.e. a real risk of covering printed
    // references/codes, not authored text about to occlude itself.
    if (pageImageInBand && hotspotsInBand > 0 && hotspotsInBand < DOCK_MIN_HOTSPOTS) {
      warnings.push({ code: "furniture_occlusion", ...where });
    }
  });

  return { errors, warnings };
}
